
import Vue from 'vue';
import BaseWelcomeStepPage from './BaseWelcomeStepPage.vue';
import Select, { SelectOption } from '~/components/builders/Base/Select.vue';

import CandidateService from '~/services/CandidateService';
import { commonProps, setupBuilder } from '~/utils/candidate';
import { CandidateJobBuilder } from '~/utils/candidate/CandidateJobBuilder';
import {
    Sector,
    CareerField,
    CandidateSector,
    CandidateCareerField,
} from '~/services/types/candidate';

export default Vue.extend({
    name: 'WelcomeStep3Page',
    components: {
        BaseWelcomeStepPage,
        Select,
    },
    props: {
        pageNumber: { type: Number, required: true },
        pageCount: { type: Number, required: true },
        clickBack: {
            type: Function,
            default: () => {},
        },
        clickNext: {
            type: Function,
            default: () => {},
        },
        error: {
            type: Function,
            default: () => {},
        },

        ...commonProps(),
    },
    data() {
        return {
            builder: null as CandidateJobBuilder | null,
            service: new CandidateService(),

            sectors: [] as CandidateSector[],
            careerFields: [] as CandidateCareerField[],

            selectedSector: '',
            selectedCareerField: '',

            sectorOptions: [] as SelectOption<string>[],
            careerFieldOptions: [] as SelectOption<string>[],

            isLoading: false,
        };
    },
    methods: {
        handleAddSector(sectorId: string) {
            console.log('SectorID:', sectorId);
            if (this.sectors.length >= 3) return;

            if (this.sectors.findIndex(s => s.sectorId === sectorId) !== -1) return;

            this.sectors.push({
                id: CandidateJobBuilder.generateId(),
                sectorId,
                candidateId: '',
                createdAt: new Date(),
                updatedAt: new Date(),
            });
        },
        handleRemoveSector(sectorId: string) {
            this.sectors = this.sectors.filter(sector => sector.sectorId !== sectorId);
        },
        handleAddCareerField(careerFieldId: string) {
            if (this.careerFields.length >= 3) return;
            if (this.careerFields.findIndex(c => c.careerFieldId == careerFieldId) !== -1) return;
            this.careerFields.push({
                id: CandidateJobBuilder.generateId(),
                careerFieldId,
                candidateId: '',
                createdAt: new Date(),
                updatedAt: new Date(),
            });
        },
        handleRemoveCareerField(careerFieldId: string) {
            this.careerFields = this.careerFields.filter(
                careerField => careerField.careerFieldId !== careerFieldId
            );
        },

        handleClickBack() {
            this.$emit('clickBack');
        },
        handleClickNext() {
            if (!this.builder || this.builder.isEmpty()) {
                this.$emit('clickNext');
                return;
            }

            const request = this.builder.buildRequest();

            this.isLoading = true;
            this.service
                .updateCandidateProfile(request)
                .then(() => {
                    this.isLoading = false;
                    this.$emit('clickNext');
                })
                .catch(err => {
                    this.$emit('error', err);
                })
                .finally(() => {
                    this.isLoading = false;
                });

            this.builder.reset();
        },

        async loadSectors(): Promise<Sector[]> {
            return this.service.getAllSectors();
        },
        async loadCareerFields(): Promise<CareerField[]> {
            return this.service.getAllCareerFields();
        },
    },
    computed: {
        renderedSectors(): { id: string; label: string }[] {
            return this.sectors.map(sector => {
                const sectorOption = this.sectorOptions.find(
                    option => option.value === sector.sectorId
                );
                return {
                    id: sector.sectorId,
                    label: sectorOption ? sectorOption.label : sector.sectorId,
                };
            });
        },
        renderedCareerFields(): { id: string; label: string }[] {
            return this.careerFields.map(candidateCF => {
                const careerFieldOption = this.careerFieldOptions.find(
                    option => option.value === candidateCF.careerFieldId
                );
                return {
                    id: candidateCF.careerFieldId,
                    label: careerFieldOption ? careerFieldOption.label : candidateCF.careerFieldId,
                };
            });
        },
        isFormValid(): boolean {
            return Boolean(this.careerFields.length);
        },
    },
    watch: {},
    mounted() {
        this.builder = setupBuilder.apply(this);

        this.sectors = this.builder.getPreferencesField('sectors') || ([] as CandidateSector[]);
        this.careerFields =
            this.builder.getPreferencesField('careerFields') || ([] as CandidateCareerField[]);

        this.$watch('sectors', this.builder.watchPreference('sectors'));
        this.$watch('careerFields', this.builder.watchPreference('careerFields'));

        this.isLoading = true;
        Promise.all([this.loadSectors(), this.loadCareerFields()])
            .then(([sectors, careerFields]) => {
                this.sectorOptions = sectors.map(sector => ({
                    value: sector.id,
                    label: sector.name,
                }));
                this.sectorOptions.sort((a, b) => a.label.localeCompare(b.label));
                this.careerFieldOptions = careerFields.map(careerField => ({
                    value: careerField.id,
                    label: careerField.name,
                }));
                this.careerFieldOptions.sort((a, b) => a.label.localeCompare(b.label));
            })
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                this.$emit('error', err);
            });
    },
});
